<script setup lang="ts">
import { useToast } from '@/composables/toast'
import { buildUrl, instance as api } from '@/helpers/api'
import { errToStr } from '@/helpers/tools'
import type { Dokument } from '@/models/apollon_conf_fk/Dokument'
import type { Data } from '@/models/apollon_data/Data'
import { GesuchPhase, type Gesuch } from '@/models/Gesuch'
import { computed, type Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import UploadDialog from '@/components/upload-dialog.vue'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { Beilage } from '@/models/apollon_data/Beilage'
import { type ZeusDokument } from '@/models/zeus/ZeusDokument'
import type { StepType } from '@/views/auth/gesuch/GesuchPanel.vue'

const {
    readOnly = false,
    gesuch,
    loading = false,
    zeusDokumente = [],
} = defineProps<{
    readOnly?: boolean
    gesuch: Gesuch | null
    loading?: boolean
    zeusDokumente: ZeusDokument[]
}>()

const showUploadDlg = ref(false)
const uploadDlgText = ref('')
const uploadDlgTitle = ref('')
const uploadDlgTarget: Ref<string | null> = ref(null)
const uploadDlgQuery: Ref<{ gesuch_id: number | null; beilage_ident: string | null } | null> =
    ref(null)
const showDelDlg = ref(false)
const attachmentToDel: Ref<Beilage | null> = ref(null)
const progress = ref(false)

const emit = defineEmits<{
    (e: 'gesuchdatachanged', data: Data | null): void
    (e: 'next', type: StepType | null): void
}>()

const { t } = useI18n()

const config = computed(() => {
    return gesuch?.config?.config?.dokumente || null
})
// config for the docs the user must upload:
/**
 * We create the following data structure to display the
 * configured (and later attached) beilagen:
 *
 * [
 *     // each conf.config.beilagen entry creates the strcuture:
 *     {
 *         conf: DokumentBeilage config,
 *         data: Beilage config, if already present
 *     },
 *     ...
 * ]
 */
const gesuchBeilagenConf = computed(() => {
    const beilagen: {
        conf: Dokument
        data: Beilage | null
        editable: boolean
    }[] = []
    if (config.value && config.value.beilagen && config.value.beilagen.docs) {
        config.value?.beilagen?.docs.forEach((conf: Dokument) => {
            if (gesuch?.phase === GesuchPhase.ABSCHLUSS) {
                const data = getBeilageData(conf.ident)
                const editable = !conf.sichtbarkeit || conf.sichtbarkeit === gesuch.phase
                if (data || editable) {
                    beilagen.push({
                        conf: conf,
                        data: data,
                        editable: editable,
                    })
                }
            } else {
                if (!conf.sichtbarkeit || conf.sichtbarkeit === gesuch?.phase) {
                    beilagen.push({
                        conf: conf,
                        data: getBeilageData(conf.ident),
                        editable: true,
                    })
                }
            }
        })
    }
    // Sort by order:
    beilagen.sort((b1, b2) => Number(b1.conf.order) - Number(b2.conf.order))
    return beilagen
})

const gesuchBeilageData = computed(() => {
    return gesuch?.data?.beilagen || []
})

const divDokumenteConf = computed(() => {
    const docs: Dokument[] = []
    if (config.value && config.value.div_dokumente && config.value.div_dokumente.docs) {
        config.value.div_dokumente.docs.forEach((conf) => {
            if (!conf.sichtbarkeit || conf.sichtbarkeit === gesuch?.phase) {
                docs.push(conf)
            }
        })
    }
    // Sort by order:
    docs.sort((b1, b2) => Number(b1.order) - Number(b2.order))
    return docs
})

// const phaseEinreichung = computed(() => {
//     if (gesuch) {
//         return gesuch.phase === GesuchPhase.EINREICHUNG
//     }
//     return false
// })

/**
 * Checks if the Gesuch data already contains
 * an uploaded document for the given beilage ident:
 *
 * @return Beilage data config if the Gesuch already has the Beilage identified by the given ident,
 *                 null if this is not the case.
 */
function getBeilageData(beilageIdent: string | null) {
    const beilagen = gesuchBeilageData

    for (let i = 0; i < beilagen.value.length; i++) {
        const data = beilagen.value[i]
        if (beilageIdent === data.ident) {
            return data
        }
    }
    return null
}

function findBeilagenConfByIdent(ident: string | null | undefined) {
    for (let i = 0; i < gesuchBeilagenConf.value.length; i++) {
        if (gesuchBeilagenConf.value[i].conf.ident === ident) {
            return gesuchBeilagenConf.value[i]
        }
    }
    return null
}

function onUnassignedBeilageClick(beilageConf: Dokument) {
    uploadDlgTitle.value = beilageConf.name || ''
    uploadDlgText.value = t('choose_upload_doc')
    uploadDlgTarget.value = buildUrl('/gesuch/uploadBeilage')
    uploadDlgQuery.value = {
        gesuch_id: gesuch?.id || null,
        beilage_ident: beilageConf.ident || null,
    }
    showUploadDlg.value = true
}

function onAssignedBeilageClick(beilageData: Beilage) {
    const url = buildUrl('/gesuch/downloadBeilage', {
        ident: beilageData.ident,
        gesuch_id: gesuch?.id,
    })
    window.open(url, 'apollon-dl')
}

function onFileUploadSuccess(beilage: Beilage | null) {
    showUploadDlg.value = false
    const beilagenConf = findBeilagenConfByIdent(beilage?.ident)
    if (!beilagenConf || !beilage) {
        return
    }
    // Store the new Beilage info on gesuch.data.beilagen[],
    // make sure only one with the same ident exists:
    if (gesuch?.data?.beilagen) {
        const beilagen = gesuch?.data?.beilagen.filter((conf) => conf.ident !== beilage.ident)
        beilagen.push(beilage)
        gesuch?.data?.set('beilagen', beilagen)
    }

    // and also update the beilagenConf for the UI:
    beilagenConf.data = beilage

    // Finally, inform about the change:
    emit('gesuchdatachanged', gesuch?.data || null)
}

function onFileUploadFailed() {
    showUploadDlg.value = false
}

function onRemoveBtn(beilageData: Beilage | null) {
    attachmentToDel.value = beilageData
    showDelDlg.value = true
}

async function onDelAttachmentConfirm() {
    if (attachmentToDel.value) {
        progress.value = true
        try {
            await api().get('/gesuch/deleteBeilage', {
                params: {
                    gesuch_id: gesuch?.id,
                    ident: attachmentToDel.value.ident,
                },
            })
            removeBeilageEntry(attachmentToDel.value)
            // Finally, inform about the change:
            emit('gesuchdatachanged', gesuch?.data || null)
        } catch (e) {
            useToast().add({ text: errToStr(e), color: 'error' })
        } finally {
            progress.value = false
            showDelDlg.value = false
            attachmentToDel.value = null
        }
    }
}

function removeBeilageEntry(beilageInfo: Beilage | null) {
    gesuch?.data?.set(
        'beilagen',
        gesuch.data.beilagen.filter((beilage) => beilage !== beilageInfo),
    )
}

function onDivDokumentClick(docInfo: Dokument | null) {
    const url = buildUrl('/gesuch/downloadDivDokumentAttachment', {
        ident: docInfo?.ident,
        gesuch_id: gesuch?.id,
    })
    window.open(url, 'apollon-dl')
}

function onZeusDokumentClick(zeusDokument: ZeusDokument | null) {
    const url = buildUrl('/gesuch/downloadZeusDokument', {
        dokument_id: zeusDokument?.id,
    })
    window.open(url, 'apollon-dl')
}
</script>

<template>
    <v-skeleton-loader
        v-if="!gesuch"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <v-container v-else fluid>
        <v-card v-if="gesuchBeilagenConf.length" class="centered-card">
            <v-card-title>{{ config?.beilagen?.label || 'Hochzuladende Dokumente' }}</v-card-title>
            <v-card-subtitle
                v-if="config?.beilagen && config?.beilagen.infotext"
                v-html="config?.beilagen.infotext.replace('\n', '<br />')"
            ></v-card-subtitle>
            <v-alert
                v-if="readOnly"
                class="mx-2"
                type="warning"
                border="start"
                colored-border
                variant="outlined"
            >
                <span v-html="$t('document_edit_msg')"></span>
            </v-alert>
            <v-list dense class="pt-0">
                <template v-for="(item, i) in gesuchBeilagenConf">
                    <template v-if="item.data">
                        <!-- already uploaded: -->
                        <v-list-item :key="i" dense @click.stop="onAssignedBeilageClick(item.data)">
                            <template #prepend>
                                <v-icon>fas fa-paperclip</v-icon>
                            </template>
                            <v-list-item-title>
                                <span class="accept--text">{{ item.conf.name }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>{{
                                item.data.originalFilename
                            }}</v-list-item-subtitle>
                            <template #append>
                                <template v-if="!readOnly && item.editable">
                                    <v-btn
                                        variant="text"
                                        size="small"
                                        icon="fas fa-trash"
                                        color="error"
                                        @click.stop="onRemoveBtn(item.data)"
                                    >
                                    </v-btn>
                                </template>
                                <template v-else>
                                    <v-icon class="mr-2" color="action white--text" size="small"
                                        >fas fa-download</v-icon
                                    >
                                </template>
                            </template>
                        </v-list-item>
                    </template>
                    <template v-else>
                        <!-- not yet uploaded: -->
                        <v-list-item
                            :key="i"
                            dense
                            :disabled="readOnly"
                            @click.stop="onUnassignedBeilageClick(item.conf)"
                        >
                            <template #prepend>
                                <v-icon>fas fa-paperclip</v-icon>
                            </template>
                            <v-list-item-title>
                                {{ item.conf.name }}
                                <span v-if="item.conf.required" class="require-label required">{{
                                    $t('required')
                                }}</span>
                                <span v-else class="require-label optional">{{
                                    $t('optional')
                                }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>{{
                                item.conf.beschreibung
                            }}</v-list-item-subtitle>

                            <template #append>
                                <template v-if="!readOnly">
                                    <v-icon class="mr-2" color="action white--text" size="small"
                                        >fas fa-upload</v-icon
                                    >
                                </template>
                            </template>
                        </v-list-item>
                    </template>
                </template>
            </v-list>
        </v-card>

        <v-card v-if="zeusDokumente.length > 0" class="mt-5 centered-card">
            <v-card-title>{{ $t('docs_energiefachstelle') }}</v-card-title>
            <v-list dense class="pt-0">
                <template v-for="item in zeusDokumente" :key="item.id">
                    <v-list-item dense @click.stop="onZeusDokumentClick(item)">
                        <template #prepend>
                            <v-icon>fas fa-file-alt</v-icon>
                        </template>
                        <v-list-item-title>
                            {{ item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-html="item.beschreibung"></v-list-item-subtitle>
                        <v-list-item-subtitle v-if="item.bearbeitet_am?.isValid()">
                            {{ item.bearbeitet_am?.format('DD.MM.YYYY') }}
                        </v-list-item-subtitle>

                        <template #append>
                            <v-icon class="mr-2" color="action white--text" size="small"
                                >fas fa-download</v-icon
                            >
                        </template>
                    </v-list-item>
                </template>
            </v-list>
        </v-card>

        <v-card v-if="divDokumenteConf.length" class="mt-5 centered-card">
            <v-card-title>{{ config?.div_dokumente?.label || 'Dokument-Download' }}</v-card-title>
            <v-card-subtitle
                v-if="config?.div_dokumente?.infotext"
                v-html="config?.div_dokumente?.infotext.replace('\n', '<br />')"
            ></v-card-subtitle>
            <v-list dense class="pt-0">
                <template v-for="(item, i) in divDokumenteConf" :key="i">
                    <v-list-item dense @click.stop="onDivDokumentClick(item)">
                        <template #prepend>
                            <v-icon>fas fa-file-alt</v-icon>
                        </template>
                        <v-list-item-title>
                            {{ item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ item.beschreibung }}</v-list-item-subtitle>

                        <template #append>
                            <v-icon class="mr-2" color="action white--text" size="small"
                                >fas fa-download</v-icon
                            >
                        </template>
                    </v-list-item>
                </template>
            </v-list>
        </v-card>
        <v-card class="centered-card mt-5" variant="flat">
            <v-card-actions>
                <v-spacer />
                <v-btn
                    variant="flat"
                    class="mt-2"
                    color="action"
                    :loading="loading"
                    @click="$emit('next', null)"
                    append-icon="fas fa-arrow-right"
                >
                    {{ $t('next_step') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <UploadDialog
            v-if="showUploadDlg"
            v-model="showUploadDlg"
            :max-width="600"
            :text="uploadDlgText"
            :title="uploadDlgTitle"
            :target="uploadDlgTarget"
            :query="uploadDlgQuery"
            @uploadsuccess="onFileUploadSuccess"
            @uploadfailed="onFileUploadFailed"
        />
        <ConfirmDlg
            v-model:model-value="showDelDlg"
            :title="$t('bestaetigung')"
            :text="$t('really_delete_attachment')"
            :loading="progress"
            :close-on-yes="false"
            @yes="onDelAttachmentConfirm"
        />
    </v-container>
</template>

<style lang="scss" scoped>
.require-label {
    font-size: 0.75em;
    &.required {
        color: red;
    }
    &.optional {
        color: blue;
    }
}
</style>
